<form [formGroup]="form.formGroup">

    <div class="pb-5 field">
      <div class="flex items-center">
        <p-checkbox
          [formControlName]="form.formControlNames.useMudPits"
          inputId="useMudPits"
          binary="true"
          (onChange)="this.useMudPits = $event.checked;"
        ></p-checkbox>
        <label for="useMudPits" class="ml-2">Circulate through Mud Pits</label>
      </div>
    </div>

    <p-tabs value="0" (valueChange)="selectedTabChange.emit($event)">
      <p-tablist>
        <p-tab value="0">Operation Details</p-tab>
        @if (showLandingString && showRiser) {
          <p-tab value="1">Landing String
            @if (landingStringMissing) {
              <i class="validation-error pi pi-exclamation-circle pl-1" pTooltip="Landing string not specified."></i>
            }
          </p-tab>
        }
        @if (showRiser) {
          <p-tab value="2">Riser
            @if (form.formGroup.controls?.insideDiameter.invalid || form.formGroup.controls?.outsideDiameter.invalid) {
              <i class="validation-error pi pi-exclamation-circle pl-1" pTooltip="Operation not saved, some required inputs not specified."></i>
            }
          </p-tab>
        }
        @if (useMudPits) {
          <p-tab value="3">Mud Pits
            @if (form.formGroup.controls.mudPits.invalid) {
              <i class="validation-error pi pi-exclamation-circle pl-1" pTooltip="Operation not saved, double check Mud Pit inputs."></i>
            }
          </p-tab>
        }
      </p-tablist>

      <p-tabpanels class="pl-4 pr-2">
      <p-tabpanel class="mt-2" value="0"> 
        <div class="flex flex-wrap gap-4">
          <!-- Left Column -->
          <div class="flex flex-col flex-1 min-w-[200px] space-y-2">
  
            <div class="flex flex-col">
              <label for="fluid">Fluid</label>
              <p-select
                filter
                filterBy="state.name"
                resetFilterOnHide="true"
                [disabled]="isReadOnly"
                [options]="fluids"
                optionLabel="state.name"
                [formControlName]="form.formControlNames.fluid"
                dataKey="id"
                appendTo="body"
                tooltipPosition="top"
                (onChange)="fluidChangedEvent($event)"
              >
                <ng-template pTemplate="selectedItem">
                  {{ form.formGroup.value.fluid?.state.name }}
                </ng-template>
                <ng-template let-fluid pTemplate="item">
                  {{ fluid.state.name }}
                </ng-template>
              </p-select>
            </div>
            <div class="flex flex-col">
              <label for="pressure">Pressure</label>
              <p-inputgroup>
                <p-select
                  [disabled]="isReadOnly"
                  [options]="pressureLocations"
                  [formControlName]="form.formControlNames.pressureLocation"
                  optionLabel="name"
                  optionValue="value"
                  appendTo="body"
                ></p-select>
                <p-inputnumber
                  [minFractionDigits]="1"
                  [formControlName]="form.formControlNames.pressure"
                />
                <p-inputgroup-addon>{{ pressureUnit }}</p-inputgroup-addon>
                @if (form.formGroup.controls.pressure.value && form.formGroup.controls.pressure.invalid) {
                  <p class="validation-error">
                    Allowed range: {{ pressureValidation.min }} - {{ pressureValidation.max }} {{ pressureUnit }}
                  </p>
                }
              </p-inputgroup>
            </div>
            <div class="flex flex-col">
              <label for="inletTemperature">Inlet Temperature</label>
              @if (!useMudPits) {
                <p-inputgroup>
                  <p-inputnumber
                    (onBlur)="setDefaultSurfaceTemperature()"
                    [minFractionDigits]="1"
                    [formControlName]="form.formControlNames.inletTemperature"
                  />
                  <p-inputgroup-addon>{{ tempUnit }}</p-inputgroup-addon>
                </p-inputgroup>
                @if (form.formGroup.controls.inletTemperature.value && form.formGroup.controls.inletTemperature.invalid) {
                  <p class="validation-error">
                    Allowed range: {{ temperatureValidation.min }} - {{ temperatureValidation.max }} {{ tempUnit }}
                  </p>
                }
              }

              @if (useMudPits) {
                <input type="number" pInputText placeholder="Mud Pits enabled" [disabled]="true" />
              }
            </div>

            @if (isSubsea && currentTubularIndex > 0) {
            <div class="pt-3">
              <p-checkbox
                  size="small" 
                  [formControlName]="form.formControlNames.includeRiser"
                  inputId="includeRiser"
                  binary="true"
                  (onChange)="hasRiserChange();"
                ></p-checkbox>
                <label class="pl-2" for="includeRiser">Include Riser</label>
            </div>
            }
          </div>
  
          <!-- Right Column -->
          <div class="flex flex-col flex-1 min-w-[150px] space-y-2">
            <div class="flex flex-col">
              <label for="circulationMd">Circulation Depth</label>
              <p-inputgroup>
                <p-inputnumber
                  [minFractionDigits]="1"
                  [formControlName]="form.formControlNames.circulationMd"
                />
                <p-inputgroup-addon>{{ depthUnit }}</p-inputgroup-addon>
              </p-inputgroup>
              @if (form.formGroup.controls.circulationMd.invalid) {
                <p class="validation-error">
                  Max value: {{ maxCircDepth }}{{ depthUnit }}
                </p>
              }
            </div>
            <div class="flex flex-col">
              <label for="flowDirection">Circulation Direction</label>
              <p-select
                  [disabled]="isReadOnly"
                  [options]="circulationDirections"
                  [formControlName]="form.formControlNames.circulationDirection"
                  optionLabel="name"
                  optionValue="value"
                  appendTo="body"
              ></p-select>
            </div>
  
            <div class="flex flex-col">
                <app-standard-fluid
                  [formControlName]="form.formControlNames.standardFluidFlow"
                  [userUnits]="userUnits"
                ></app-standard-fluid>
            </div>
            
          </div>
          </div>
      </p-tabpanel>

      <p-tabpanel value="1">
        <p class="{{ landingStringMissing ? 'validation-error' : '' }}">{{ selectedLandingString }}</p>
      </p-tabpanel>

      @if (showRiser) {
        <p-tabpanel value="2">
          <p-fieldset legend="Riser">
          <div class="space-y-2">
            <div class="flex flex-col">
              <label for="outsideDiameter">Outside Diameter</label>
              <p-inputgroup>
                <p-inputnumber [minFractionDigits]="1" [formControlName]="form.formControlNames.outsideDiameter" />
                <p-inputgroup-addon>{{ pipeRoughnessUnit }}</p-inputgroup-addon>
              </p-inputgroup>
            </div>

            <div class="flex flex-col">
              <label for="outsideDiameter">Inside Diameter</label>
              <p-inputgroup>
                <p-inputnumber [minFractionDigits]="1" [formControlName]="form.formControlNames.insideDiameter" />
                <p-inputgroup-addon>{{ pipeRoughnessUnit }}</p-inputgroup-addon>
              </p-inputgroup>
            </div>
          </div>
          </p-fieldset>
        </p-tabpanel>
      }

      <p-tabpanel value="3">
        @if (useMudPits) {
          <app-mud-pit-details
            [disableMudPits]="!useMudPits"
            [previousOperationMudPits]="previousOperationMudPits"
            [userUnits]="userUnits"
            [formControlName]="form.formControlNames.mudPits"
              [surfaceTemperature]="surfaceTemperature"
          ></app-mud-pit-details>
        }
      </p-tabpanel>

    </p-tabpanels>
  </p-tabs>
  
</form>
